import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, ChevronRight } from "lucide-react";
import BrandMark from "../brand/BrandMark";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_NAV, isNavItemActive } from "../../lib/adminNav";

const EnvBadge = () => {
  const isProd = typeof window !== "undefined" && window.location.hostname === "www.avisocar.com";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${
        isProd
          ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/40"
          : "bg-orange-500/15 text-orange-300 border-orange-400/40"
      }`}
      data-testid="admin-env-badge"
    >
      <span className={`w-2 h-2 rounded-full ${isProd ? "bg-emerald-400" : "bg-orange-400"}`} />
      {isProd ? "PRODUCCIÓN" : "PREVIEW"}
    </span>
  );
};

const NavList = ({ pathname, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-6" data-testid="admin-nav">
    {ADMIN_NAV.map((group) => (
      <div key={group.id}>
        <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          {group.label}
        </p>
        <div className="space-y-0.5">
          {group.items.map((item) => {
            const active = isNavItemActive(item, pathname);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${
                  active
                    ? "bg-[#1B84FF]/15 text-white font-semibold ring-1 ring-inset ring-[#1B84FF]/40"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={item.testid}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-[#5BC0FF]" : ""}`} aria-hidden="true" />
                <span className="truncate">{item.label}</span>
                {active && <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#5BC0FF]" aria-hidden="true" />}
              </Link>
            );
          })}
        </div>
      </div>
    ))}
  </nav>
);

/**
 * Control tower shell: one navigation, one header, consistent spacing.
 * Pages pass their own title/description/actions and keep their own content.
 */
export default function AdminShell({ title, description, actions, children, testid, flush }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#071B2F]" data-testid={testid || "admin-shell"}>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-white/10 bg-[#050F1D] lg:flex">
        <div className="px-6 pt-6 pb-5">
          <BrandMark size="sm" onDark asLink={false} />
          <p className="mt-2 text-xs text-slate-500">Torre de control · Master Admin</p>
          <div className="mt-3">
            <EnvBadge />
          </div>
        </div>
        <NavList pathname={pathname} />
        <div className="border-t border-white/10 p-4">
          <div className="mb-2 px-1">
            <p className="truncate text-sm text-white">{user?.full_name}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:bg-white/5 hover:text-white"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#050F1D]/95 backdrop-blur lg:hidden">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              className="rounded-lg p-2 text-slate-300 hover:bg-white/10"
              aria-label="Abrir menú de administración"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <BrandMark size="xs" onDark asLink={false} />
          </div>
          <EnvBadge />
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
          <button
            className="absolute inset-0 bg-black/70"
            onClick={() => setOpen(false)}
            aria-label="Cerrar menú"
            tabIndex={-1}
          />
          <div className="absolute inset-y-0 left-0 flex w-[82%] max-w-xs flex-col bg-[#050F1D] shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <BrandMark size="xs" onDark asLink={false} />
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-slate-300 hover:bg-white/10"
                aria-label="Cerrar menú"
                data-testid="admin-mobile-menu-close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <NavList pathname={pathname} onNavigate={() => setOpen(false)} />
            <div className="border-t border-white/10 p-4">
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-400"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" /> Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="min-w-0 lg:pl-72">
        <div className={flush ? "" : "mx-auto max-w-[1400px] px-4 py-8 sm:px-8 sm:py-10"}>
          {(title || actions) && (
            <div className="mb-8 flex flex-col gap-4 border-b border-white/10 pb-6 md:flex-row md:items-end md:justify-between">
              <div>
                {title && (
                  <h1
                    className="text-2xl font-bold tracking-tight text-white sm:text-3xl"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                    data-testid="admin-page-title"
                  >
                    {title}
                  </h1>
                )}
                {description && (
                  <p className="mt-2 max-w-2xl text-sm text-slate-400 md:text-base">{description}</p>
                )}
              </div>
              {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
